import React, { useEffect, useState, useCallback } from "react";
import { api, API_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { usePageTitle } from "@/lib/usePageTitle";
import { TradeCategoryPicker, tradeLabel } from "@/components/TradeCategoryPicker";

const TABS = [["overview", "Overview"], ["onboarding", "Onboarding"], ["offers", "Job offers"], ["jobs", "My jobs"], ["payouts", "Payouts"]];

function Toggle({ label, desc, on, onToggle, testid }) {
  return (
    <div className="togglerow">
      <div><b>{label}</b><span>{desc}</span></div>
      <button className={`switch ${on ? "on" : ""}`} data-testid={testid} onClick={onToggle} aria-label={label}><i /></button>
    </div>
  );
}

function timeLeft(iso) {
  const ms = new Date(iso) - new Date();
  if (ms <= 0) return "expiring";
  const h = Math.floor(ms / 3.6e6);
  return h >= 1 ? `${h}h left to decide` : "under 1h left";
}

export default function TradeDashboard() {
  const { user } = useAuth();
  usePageTitle("Partner portal — Olivia Does");
  const [tab, setTab] = useState("overview");
  const [ov, setOv] = useState(null);
  const [offers, setOffers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [onb, setOnb] = useState(null);
  const [uploading, setUploading] = useState(null);
  const [prefs, setPrefs] = useState({ sms: true, whatsapp: false, email: true });
  const [quoteFor, setQuoteFor] = useState(null);
  const [quote, setQuote] = useState({ amount: "", eta_days: "", message: "" });

  const load = useCallback(() => {
    api.get("/contractor/overview").then((r) => setOv(r.data)).catch(() => {});
    api.get("/offers").then((r) => setOffers(r.data)).catch(() => {});
    api.get("/jobs").then((r) => setJobs(r.data.filter((j) => j.assignee_id === user.id))).catch(() => {});
    api.get("/payouts").then((r) => setPayouts(r.data)).catch(() => {});
    api.get("/partner/onboarding").then((r) => setOnb(r.data)).catch(() => {});
  }, [user.id]);

  useEffect(() => { load(); api.get("/me/notifications").then((r) => setPrefs(r.data)).catch(() => {}); }, [load]);

  const savePrefs = (next) => { setPrefs(next); api.patch("/me/notifications", next).catch(() => toast.error("Couldn't save")); };

  const act = async (id, action) => {
    try { await api.post(`/offers/${id}/${action}`); toast.success(action === "accept" ? "Job accepted — it's in My jobs." : "Offer declined."); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not update offer"); load(); }
  };

  const submitQuote = async (jobId) => {
    try {
      await api.post(`/jobs/${jobId}/quotes`, { amount: Number(quote.amount), eta_days: Number(quote.eta_days), message: quote.message });
      toast.success("Quote sent to the customer.");
      setQuoteFor(null); setQuote({ amount: "", eta_days: "", message: "" }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send quote"); }
  };

  const complete = async (jobId) => {
    try { await api.patch(`/jobs/${jobId}/status`, { status: "completed" }); toast.success("Marked complete — payout queued."); load(); }
    catch (e) { toast.error("Could not update"); }
  };

  const uploadDoc = async (docType, file) => {
    if (!file) return;
    setUploading(docType);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/partner/documents/${docType}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded.");
      api.get("/partner/onboarding").then((r) => setOnb(r.data)).catch(() => {});
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setUploading(null); }
  };

  const remainingDocs = onb ? onb.items.filter((i) => !i.uploaded).length : 0;

  return (
    <section className="od-section od-wrap" data-testid="trade-dashboard">
      <div className="od-eyebrow">Partner portal</div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, margin: "6px 0 24px" }}>
        <div>
          <h1 className="serif" style={{ fontSize: "2rem", fontWeight: 500, margin: 0 }}>{user.name}</h1>
          <p style={{ color: "var(--taupe)", marginTop: 4 }}>Olivia Partner Network</p>
          <div style={{ color: "var(--taupe)", marginTop: 4 }}><TradeCategoryPicker user={user} /></div>
        </div>
        {ov?.standing && <span className="spotpill" style={{ background: "var(--rose-soft)", color: "var(--rose-deep)" }} data-testid="trade-standing">{ov.standing}</span>}
      </div>

      <div className="pillrow" style={{ marginBottom: 30 }}>
        {TABS.map(([v, label]) => (
          <div key={v} data-testid={`trade-tab-${v}`} className={`pill ${tab === v ? "on" : ""}`} onClick={() => setTab(v)}>
            {label}{v === "offers" && offers.length ? ` (${offers.length})` : ""}{v === "onboarding" && remainingDocs ? ` (${remainingDocs})` : ""}
          </div>
        ))}
      </div>

      {/* OVERVIEW */}
      {tab === "overview" && ov && (
        <div data-testid="trade-overview">
          <div className="ogrid cols-3" style={{ marginBottom: 26 }}>
            <div className="statcard"><b style={{ fontSize: "1.6rem" }}>{ov.acceptance_rate != null ? `${ov.acceptance_rate}%` : "—"}</b><span>Offer acceptance rate</span></div>
            <div className="statcard"><b style={{ fontSize: "1.6rem" }}>{ov.completed_jobs}</b><span>Jobs completed</span></div>
            <div className="statcard"><b style={{ fontSize: "1.6rem" }}>{ov.avg_rating ?? "—"}{ov.avg_rating ? "★" : ""}</b><span>{ov.reviews} reviews</span></div>
            <div className="statcard"><b style={{ fontSize: "1.6rem" }}>£{"\u2009"}{ov.total_earned}</b><span>Paid out to date</span></div>
            <div className="statcard"><b style={{ fontSize: "1.6rem" }}>£{"\u2009"}{ov.pending_payout}</b><span>Pending payout</span></div>
            <div className="statcard"><b style={{ fontSize: "1.6rem" }}>{ov.active_jobs}</b><span>Active jobs</span></div>
          </div>
          <div className="card" style={{ marginBottom: 24 }}>
            <h3 style={{ marginTop: 0, fontWeight: 500 }}>How you get paid</h3>
            <p style={{ color: "var(--taupe)" }}>Olivia takes a flat {ov.commission_pct}% fee — nothing else is deducted. You keep the rest, paid by direct deposit within 24–48 hours of a job being marked complete. On a £120 job, that's £{(120 * (1 - ov.commission_pct / 100)).toFixed(0)} to you.</p>
          </div>
          <div className="togglecard">
            <div className="od-eyebrow" style={{ marginBottom: 4 }}>Notifications</div>
            <Toggle label="SMS alerts" desc="New offers and job updates by text" on={prefs.sms} onToggle={() => savePrefs({ ...prefs, sms: !prefs.sms })} testid="pref-sms" />
            <Toggle label="WhatsApp alerts" desc="Same alerts via WhatsApp" on={prefs.whatsapp} onToggle={() => savePrefs({ ...prefs, whatsapp: !prefs.whatsapp })} testid="pref-whatsapp" />
            <Toggle label="Email alerts" desc="Offers, approvals and payout confirmations" on={prefs.email} onToggle={() => savePrefs({ ...prefs, email: !prefs.email })} testid="pref-email" />
          </div>
        </div>
      )}

      {/* ONBOARDING */}
      {tab === "onboarding" && (
        <div data-testid="trade-onboarding">
          {(() => {
            const st = onb?.state;
            const good = st === "verified" || st === "exempt";
            const pending = st === "under_review";
            const bg = good ? "var(--rose-soft)" : pending ? "var(--cream-deep)" : "#FBEFEF";
            const bc = good ? undefined : pending ? "var(--hairline)" : "#E9CFCF";
            const copy = {
              verified: ["You're verified and ready for work", "Thank you — all your documents have been approved. You can accept job offers as they come in."],
              exempt: ["Your account is already active", "As an established partner you can already accept jobs. Adding your documents below keeps your compliance paperwork on file with Olivia."],
              under_review: ["Documents received — under review", "Thanks! Our team is reviewing your documents. You'll be able to accept job offers as soon as they're approved."],
              rejected: ["Action needed — please re-upload", "One or more documents couldn't be verified. Please re-upload the flagged items below to continue."],
              incomplete: ["Complete your onboarding to start accepting jobs", "Every Olivia Does partner is vetted. Upload the documents below — you'll be approved for work once our team has verified them."],
            }[st] || ["Complete your onboarding to start accepting jobs", "Upload your documents below to get started."];
            return (
              <div className="card" data-testid="onb-banner" style={{ marginBottom: 24, background: bg, borderColor: bc }}>
                <h3 style={{ marginTop: 0, fontWeight: 500 }}>{copy[0]}</h3>
                <p style={{ color: "var(--taupe)", margin: 0 }}>{copy[1]}</p>
              </div>
            );
          })()}
          {onb && onb.items.map((it) => {
            const badge = { verified: ["done", "Approved"], submitted: ["quote", "Under review"], rejected: ["requested", "Rejected"], missing: ["requested", "Missing"] }[it.status] || ["requested", "Missing"];
            return (
            <div className="jobcard" key={it.doc_type} data-testid={`onb-item-${it.doc_type}`}>
              <div className="head">
                <div>
                  <div className="od-eyebrow" style={{ marginBottom: 4 }}>Required document</div>
                  <h3 style={{ margin: 0, fontWeight: 500 }}>{it.label}</h3>
                </div>
                <span className={`status-badge ${badge[0]}`} data-testid={`onb-status-${it.doc_type}`}>{badge[1]}</span>
              </div>
              {it.status === "rejected" && it.review_note && (
                <p style={{ fontSize: ".82rem", color: "#B04A4A", marginTop: 8 }} data-testid={`onb-note-${it.doc_type}`}>⚠ {it.review_note}</p>
              )}
              {it.document && (
                <p style={{ fontSize: ".82rem", color: "var(--taupe)", marginTop: 8 }}>
                  📎 {it.document.original_filename}
                  {" · "}
                  <a href={`${API_BASE}/partner/documents/${it.document.id}/download`} target="_blank" rel="noreferrer" className="od-link" data-testid={`onb-view-${it.doc_type}`}>View</a>
                </p>
              )}
              <div style={{ marginTop: 12 }}>
                <label className="btn secondary sm" style={{ cursor: "pointer", display: "inline-block" }}>
                  {uploading === it.doc_type ? "Uploading…" : it.document ? "Replace file" : "Upload file"}
                  <input
                    type="file"
                    data-testid={`onb-upload-${it.doc_type}`}
                    accept=".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/*"
                    style={{ display: "none" }}
                    disabled={uploading === it.doc_type}
                    onChange={(e) => uploadDoc(it.doc_type, e.target.files?.[0])}
                  />
                </label>
                <span style={{ fontSize: ".78rem", color: "var(--taupe)", marginLeft: 10 }}>PDF or image, up to 10MB</span>
              </div>
            </div>
            );
          })}
        </div>
      )}

      {/* OFFERS */}
      {tab === "offers" && (
        <div data-testid="trade-offers">
          {onb && !onb.complete && (
            <div className="card" data-testid="offers-onboarding-gate" style={{ marginBottom: 20, background: "#FBEFEF", borderColor: "#E9CFCF" }}>
              <p style={{ margin: 0, color: "var(--taupe)" }}>
                {onb.state === "under_review"
                  ? "Your documents are with our team for review — you'll be able to accept offers as soon as they're approved."
                  : onb.state === "rejected"
                  ? <>A document needs re-uploading. Open your <b onClick={() => setTab("onboarding")} style={{ cursor: "pointer", color: "var(--rose-deep)" }}>onboarding checklist</b> to fix it before accepting offers.</>
                  : <>Finish your <b onClick={() => setTab("onboarding")} style={{ cursor: "pointer", color: "var(--rose-deep)" }}>onboarding checklist</b> to accept offers. You can review and decline offers in the meantime.</>}
              </p>
            </div>
          )}
          {offers.length === 0 ? (
            <div className="card" style={{ textAlign: "center", color: "var(--taupe)", padding: 40 }}>No open offers right now — we'll alert you the moment a {tradeLabel(user) || "matching"} job comes in.</div>
          ) : offers.map((o) => (
            <div className="jobcard" key={o.id} data-testid={`offer-${o.id}`}>
              <div className="head">
                <div><div className="od-eyebrow" style={{ marginBottom: 4 }}>{o.category} · {o.urgency}</div><h3 style={{ margin: 0, fontWeight: 500 }}>{o.job_title}</h3></div>
                <span className="status-badge quote">{timeLeft(o.expires_at)}</span>
              </div>
              <p style={{ marginTop: 10, color: "var(--taupe)" }}>{o.description}</p>
              <p style={{ fontSize: ".82rem", color: "var(--taupe)", marginTop: 6 }}>📍 {o.address}</p>
              <div className="quotebox">
                <div><span style={{ fontSize: ".78rem", color: "var(--taupe)" }}>{o.net ? "You'd net (after 10% fee)" : "Submit a quote to win this"}</span>{o.net && <div className="amt">£{o.net}</div>}</div>
                <div style={{ display: "flex", gap: 10 }}>
                  <button className="btn secondary sm" data-testid={`offer-decline-${o.id}`} onClick={() => act(o.id, "decline")}>Decline</button>
                  <button className="btn rose sm" data-testid={`offer-accept-${o.id}`} onClick={() => act(o.id, "accept")}>Accept job</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* MY JOBS */}
      {tab === "jobs" && (
        <div data-testid="trade-jobs">
          {jobs.length === 0 ? (
            <div className="card" style={{ textAlign: "center", color: "var(--taupe)", padding: 40 }}>No jobs assigned yet — accept an offer to get started.</div>
          ) : jobs.map((j) => {
            const needsQuote = j.job_type === "quote" && j.status === "requested";
            const canComplete = ["scheduled", "in_progress"].includes(j.status);
            return (
              <div className="jobcard" key={j.id} data-testid={`trade-job-${j.id}`}>
                <div className="head">
                  <div><div className="od-eyebrow" style={{ marginBottom: 4 }}>{j.category}</div><h3 style={{ margin: 0, fontWeight: 500 }}>{j.title}</h3></div>
                  <span className={`status-badge ${j.status === "completed" ? "done" : j.status === "quoted" ? "quote" : "matched"}`}>{j.status.replace("_", " ")}</span>
                </div>
                <p style={{ marginTop: 10, color: "var(--taupe)" }}>{j.description}</p>
                <p style={{ fontSize: ".82rem", color: "var(--taupe)", marginTop: 6 }}>📍 {j.address}{j.price ? ` · £${j.price}` : ""}</p>
                {needsQuote && quoteFor !== j.id && (
                  <button className="btn primary sm" style={{ marginTop: 14 }} data-testid={`quote-open-${j.id}`} onClick={() => setQuoteFor(j.id)}>Submit a quote</button>
                )}
                {needsQuote && quoteFor === j.id && (
                  <div className="quotebox" style={{ display: "block" }}>
                    <div className="ogrid cols-2" style={{ gap: 12, marginBottom: 12 }}>
                      <div><label className="flabel">Amount (£)</label><input className="finput" data-testid={`quote-amount-${j.id}`} type="number" value={quote.amount} onChange={(e) => setQuote({ ...quote, amount: e.target.value })} /></div>
                      <div><label className="flabel">ETA (days)</label><input className="finput" data-testid={`quote-eta-${j.id}`} type="number" value={quote.eta_days} onChange={(e) => setQuote({ ...quote, eta_days: e.target.value })} /></div>
                    </div>
                    <textarea className="finput" placeholder="Optional message to the customer" value={quote.message} onChange={(e) => setQuote({ ...quote, message: e.target.value })} />
                    <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
                      <button className="btn secondary sm" onClick={() => setQuoteFor(null)}>Cancel</button>
                      <button className="btn rose sm" data-testid={`quote-send-${j.id}`} disabled={!quote.amount || !quote.eta_days} onClick={() => submitQuote(j.id)}>Send quote</button>
                    </div>
                  </div>
                )}
                {canComplete && (
                  <button className="btn rose sm" style={{ marginTop: 14 }} data-testid={`complete-${j.id}`} onClick={() => complete(j.id)}>Mark complete</button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* PAYOUTS */}
      {tab === "payouts" && (
        <div data-testid="trade-payouts">
          {payouts.length === 0 ? (
            <div className="card" style={{ textAlign: "center", color: "var(--taupe)", padding: 40 }}>No payouts yet — complete a job and your earnings appear here.</div>
          ) : (
            <div className="card" style={{ padding: 0, overflow: "hidden" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: ".9rem" }}>
                <thead><tr style={{ textAlign: "left", background: "var(--cream-deep)" }}>
                  <th style={{ padding: "14px 18px" }}>Job</th><th style={{ padding: "14px 18px" }}>Gross</th><th style={{ padding: "14px 18px" }}>Fee</th><th style={{ padding: "14px 18px" }}>Net</th><th style={{ padding: "14px 18px" }}>Status</th>
                </tr></thead>
                <tbody>
                  {payouts.map((p) => (
                    <tr key={p.id} data-testid={`payout-${p.id}`} style={{ borderTop: "1px solid var(--hairline)" }}>
                      <td style={{ padding: "14px 18px" }}>{p.job_title}</td>
                      <td style={{ padding: "14px 18px" }}>£{p.gross}</td>
                      <td style={{ padding: "14px 18px", color: "var(--taupe)" }}>−£{p.fee}</td>
                      <td style={{ padding: "14px 18px", fontFamily: "'Fraunces'", fontWeight: 600 }}>£{p.net}</td>
                      <td style={{ padding: "14px 18px" }}><span className={`status-badge ${p.status === "paid" ? "matched" : "requested"}`}>{p.status === "paid" ? "Paid" : "Processing"}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
